import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Send, FileText, Mail, MessageSquare, User } from "lucide-react";

const TURNSTILE_SITE_KEY = "0x4AAAAAABsSOLdl1odAQBgC";
const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

/* El script de Cloudflare se carga sólo cuando este formulario entra en
   pantalla, no en todas las páginas: menos datos hacia terceros y una carga
   más liviana en el resto del sitio. */
function cargarTurnstile() {
  if (document.querySelector(`script[src^="${TURNSTILE_SRC}"]`)) return;
  const s = document.createElement("script");
  s.src = TURNSTILE_SRC;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

const ContactForm = ({ language }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    file: null,
  });
  const [acepta, setAcepta] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const captchaDivRef = useRef(null);
  const widgetIdRef = useRef(null);
  const trampaRef = useRef(null);

  useEffect(() => {
    cargarTurnstile();

    let intentos = 0;
    const timer = setInterval(() => {
      intentos++;
      if (!window.turnstile || !captchaDivRef.current) {
        if (intentos > 60) clearInterval(timer);
        return;
      }
      if (widgetIdRef.current !== null) {
        clearInterval(timer);
        return;
      }
      try {
        widgetIdRef.current = window.turnstile.render(captchaDivRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "dark",
          language: language === "es" ? "es" : "de",
          callback: (token) => setCaptchaToken(token),
          "expired-callback": () => setCaptchaToken(null),
          "error-callback": () => setCaptchaToken(null),
        });
        clearInterval(timer);
      } catch {
        /* reintentos */
      }
      if (intentos > 60) clearInterval(timer);
    }, 300);

    return () => {
      clearInterval(timer);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [language]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken || !acepta) {
      setSubmitStatus("error");
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const fd = new FormData();
      fd.append("name", formData.name);
      fd.append("email", formData.email);
      fd.append("message", formData.message);
      fd.append("cf-turnstile-response", captchaToken);
      fd.append("website", trampaRef.current ? trampaRef.current.value : "");
      if (formData.file) fd.append("file", formData.file);

      const res = await fetch("/api/contact", { method: "POST", body: fd });
      if (!res.ok) throw new Error("Request failed");

      setSubmitStatus("success");
      setFormData({ name: "", email: "", message: "", file: null });
      setAcepta(false);
      setCaptchaToken(null);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {}
      }
    } catch {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = {
    de: {
      title: "Schreiben Sie mir",
      intro:
        "Ob Sie einen Text lektorieren oder setzen lassen möchten, ein Buch suchen oder als Presse anfragen: schreiben Sie kurz, worum es geht. Ich antworte persönlich, meist innerhalb von zwei Tagen. (Manuskripte zur Veröffentlichung nehme ich nicht an.)",
      nameLabel: "Name",
      namePlaceholder: "Ihr Name",
      emailLabel: "E-Mail",
      emailPlaceholder: "Ihre E-Mail-Adresse",
      messageLabel: "Nachricht",
      messagePlaceholder: "Worum geht es?",
      fileLabel: "Dokument hochladen (optional)",
      fileHint: "PDF, DOC, DOCX, ODT, TXT, JPG oder PNG, bis 5 MB.",
      fileSelected: "Datei:",
      captchaLabel: "Sicherheitsprüfung",
      consent1: "Ich habe die",
      consentLink: "Datenschutzerklärung",
      consent2:
        "gelesen und bin damit einverstanden, dass meine Angaben zur Beantwortung meiner Anfrage verarbeitet werden.",
      buttonSend: "Nachricht senden",
      buttonSending: "Senden…",
      successMessage: "Danke — die Nachricht ist angekommen. Ich melde mich.",
      errorMessage:
        "Die Nachricht konnte nicht gesendet werden. Bitte Einwilligung und Sicherheitsprüfung kontrollieren, später erneut versuchen oder direkt an office@bw-verlag.at schreiben.",
    },
    es: {
      title: "Escríbeme",
      intro:
        "Tanto si quieres que corrija o maquete un texto como si buscas un libro o escribes desde un medio: cuéntame en dos líneas de qué se trata. Contesto yo mismo, normalmente en un par de días. (No recibo manuscritos para publicación.)",
      nameLabel: "Nombre",
      namePlaceholder: "Tu nombre",
      emailLabel: "Correo electrónico",
      emailPlaceholder: "Tu dirección de correo electrónico",
      messageLabel: "Mensaje",
      messagePlaceholder: "¿De qué se trata?",
      fileLabel: "Subir documento (opcional)",
      fileHint: "PDF, DOC, DOCX, ODT, TXT, JPG o PNG, hasta 5 MB.",
      fileSelected: "Archivo:",
      captchaLabel: "Verificación",
      consent1: "He leído la",
      consentLink: "política de privacidad",
      consent2:
        "y acepto que mis datos se traten para responder a esta consulta.",
      buttonSend: "Enviar mensaje",
      buttonSending: "Enviando…",
      successMessage: "Gracias — el mensaje ha llegado. Te contesto.",
      errorMessage:
        "No se ha podido enviar el mensaje. Comprueba la casilla de privacidad y la verificación, inténtalo más tarde o escribe directamente a office@bw-verlag.at.",
    },
  };
  const t = content[language];

  const campo =
    "w-full px-4 py-3 bg-tinta border border-borde text-hueso placeholder-hueso/50 focus:border-lobo focus:outline-none transition-colors";

  return (
    <section
      id="kontakt"
      className="bg-noche text-hueso py-20 sm:py-28 scroll-mt-24"
    >
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <motion.h2
          className="font-serif text-3xl sm:text-4xl tracking-titulo text-center mb-5"
          initial={{ y: -50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6 }}
        >
          {t.title}
        </motion.h2>
        <p className="text-center text-hueso/65 max-w-xl mx-auto mb-12">
          {t.intro}
        </p>

        <motion.form
          onSubmit={handleSubmit}
          className="border border-borde bg-carbon p-7 sm:p-10 space-y-7"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          {/* Campo cebo: invisible para las personas, tentador para los robots. */}
          <div className="hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              ref={trampaRef}
              type="text"
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              defaultValue=""
            />
          </div>

          <div>
            <label htmlFor="name" className="rotulo block text-hueso/70 mb-2">
              <User className="inline-block mr-2 w-4 h-4 text-lobo align-[-2px]" />{" "}
              {t.nameLabel}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={campo}
              placeholder={t.namePlaceholder}
              autoComplete="name"
              maxLength={120}
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="rotulo block text-hueso/70 mb-2">
              <Mail className="inline-block mr-2 w-4 h-4 text-lobo align-[-2px]" />{" "}
              {t.emailLabel}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={campo}
              placeholder={t.emailPlaceholder}
              autoComplete="email"
              maxLength={160}
              required
            />
          </div>

          <div>
            <label htmlFor="message" className="rotulo block text-hueso/70 mb-2">
              <MessageSquare className="inline-block mr-2 w-4 h-4 text-lobo align-[-2px]" />{" "}
              {t.messageLabel}
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="5"
              className={campo}
              placeholder={t.messagePlaceholder}
              maxLength={20000}
              required
            />
          </div>

          <div>
            <label htmlFor="file" className="rotulo block text-hueso/70 mb-2">
              <FileText className="inline-block mr-2 w-4 h-4 text-lobo align-[-2px]" />{" "}
              {t.fileLabel}
            </label>
            <input
              type="file"
              id="file"
              name="file"
              accept=".pdf,.doc,.docx,.odt,.txt,.jpg,.jpeg,.png"
              onChange={handleChange}
              className="w-full text-sm text-hueso/70 file:mr-4 file:py-2 file:px-4 file:border file:border-borde file:bg-tinta file:text-hueso/80 file:rotulo hover:file:border-lobo file:cursor-pointer"
            />
            <p className="mt-2 text-xs text-hueso/55">{t.fileHint}</p>
            {formData.file && (
              <p className="mt-1 text-sm text-hueso/60">
                {t.fileSelected} {formData.file.name}
              </p>
            )}
          </div>

          <div>
            <label className="rotulo block text-hueso/70 mb-2">
              {t.captchaLabel}
            </label>
            <div className="border border-borde bg-tinta p-3 inline-block">
              <div ref={captchaDivRef} style={{ width: 300, minHeight: 68 }} />
            </div>
          </div>

          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="consent"
              checked={acepta}
              onChange={(e) => setAcepta(e.target.checked)}
              required
              className="mt-1 w-4 h-4 shrink-0 accent-lobo"
            />
            <label htmlFor="consent" className="text-sm text-hueso/70 leading-relaxed">
              {t.consent1}{" "}
              <Link
                to={`/${language}/privacy`}
                className="underline underline-offset-4 text-loboClaro hover:text-hueso"
              >
                {t.consentLink}
              </Link>{" "}
              {t.consent2}
            </label>
          </div>

          <motion.button
            type="submit"
            className="w-full rotulo bg-hueso text-tinta px-8 py-4 hover:bg-luna transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            disabled={isSubmitting || !captchaToken || !acepta}
          >
            {isSubmitting ? (
              t.buttonSending
            ) : (
              <>
                <Send className="w-4 h-4" /> {t.buttonSend}
              </>
            )}
          </motion.button>

          <div aria-live="polite">
            {submitStatus === "success" && (
              <motion.p
                className="text-center text-loboClaro"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {t.successMessage}
              </motion.p>
            )}
            {submitStatus === "error" && (
              <motion.p
                className="text-center text-red-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {t.errorMessage}
              </motion.p>
            )}
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default ContactForm;
